<template>
  <div class="world-map-container" :class="{ 'fullscreen-mode': isFullscreen }">
    <h2 class="section-title" v-if="!isFullscreen">Genome-wide distribution of methanogenic archaea</h2>
    <div class="map-wrapper">
      <div id="methanogen-map" class="map-container"></div>
      <div class="map-legend">
        <h4>Environment Types</h4>
        <div class="legend-item">
          <span class="legend-marker water">◆</span>
          <span>Aquatic Environment ({{ envCounts.aquatic }})</span>
        </div>
        <div class="legend-item">
          <span class="legend-marker soil">▲</span>
          <span>Soil/Sediment ({{ envCounts.soil }})</span>
        </div>
        <div class="legend-item">
          <span class="legend-marker artificial">■</span>
          <span>Artificial Environment ({{ envCounts.artificial }})</span>
        </div>
        <div class="legend-item">
          <span class="legend-marker other">●</span>
          <span>Other/Unknown ({{ envCounts.other }})</span>
        </div>
      </div>
      <div v-if="loading" class="loading-overlay">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <p>Loading map data...</p>
      </div>
      <!-- Add emergency fix button -->
      <button @click="recoverMap" class="map-reset-btn" title="Click this button to fix if you encounter map loading errors">
        <el-icon><Refresh /></el-icon> Fix Map
      </button>
      <!-- Add fullscreen button -->
      <button @click="toggleFullscreen" class="map-fullscreen-btn" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen View'">
        <el-icon v-if="isFullscreen"><FullScreen /></el-icon>
        <el-icon v-else><Expand /></el-icon>
        {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
      </button>
    </div>

    <!-- Details Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="activeMarker ? activeMarker.organism_name : 'Details'"
      width="50%"
      destroy-on-close
    >
      <div v-if="activeMarker" class="marker-details">
        <div class="detail-row">
          <span class="detail-label">Organism Name:</span>
          <span class="detail-value">{{ activeMarker.organism_name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Location:</span>
          <span class="detail-value">{{ activeMarker.location }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Coordinates:</span>
          <span class="detail-value">{{ activeMarker.latitude }}°{{ activeMarker.latitude >= 0 ? 'N' : 'S' }}, {{ Math.abs(activeMarker.longitude) }}°{{ activeMarker.longitude >= 0 ? 'E' : 'W' }}</span>
        </div>
        <div class="detail-row" v-if="activeMarker.environmental_media">
          <span class="detail-label">Environmental Media:</span>
          <span class="detail-value">{{ activeMarker.environmental_media }}</span>
        </div>
        <div class="detail-row" v-if="activeMarker.metabolic_pathways">
          <span class="detail-label">Metabolic Pathways:</span>
          <div class="detail-value metabolic-pathways">
            <template v-for="(pathway, index) in splitPathways(activeMarker.metabolic_pathways)" :key="index">
              <span class="pathway-container">
                <a 
                  href="#"
                  class="pathway-link"
                  @click.prevent="openPathwayImage(pathway)"
                >{{ pathway }}</a>
                <span v-if="index < splitPathways(activeMarker.metabolic_pathways).length - 1" class="pathway-separator">, </span>
              </span>
            </template>
          </div>
        </div>
        <div class="detail-row" v-if="activeMarker.references">
          <span class="detail-label">References:</span>
          <div class="detail-value references">{{ activeMarker.references }}</div>
        </div>
        <div class="detail-row" v-if="activeMarker.doi">
          <span class="detail-label">DOI:</span>
          <a :href="formatDoiUrl(activeMarker.doi)" target="_blank" class="detail-value doi-link">
            {{ activeMarker.doi }}
            <el-icon><Link /></el-icon>
          </a>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import L from 'leaflet';
import { ElNotification } from 'element-plus';
import { Loading, Link, Refresh, FullScreen, Expand } from '@element-plus/icons-vue';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

export default {
  name: 'WorldMap',
  components: {
    Loading,
    Link,
    Refresh,
    FullScreen,
    Expand
  },
  setup() {
    const loading = ref(true);
    const mapData = ref([]);
    const mapControloo = ref(null);
    const markers = ref({});  // Store marker references
    const dialogVisible = ref(false);
    const activeMarker = ref(null);
    const isZooming = ref(false);    // Flag for zooming state
    const needsMarkerRefresh = ref(false); // Flag for marker refresh
    const isFullscreen = ref(false);  // Fullscreen state
    const envCounts = ref({
      aquatic: 0,
      soil: 0,
      artificial: 0,
      other: 0
    });
    let zoomTimer = null;           // Zoom timer
    
    // Create debounce function - avoid multiple executions in short time
    const debounce = (fn, delay) => {
      let timer = null;
      return function(...args) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    };



   const setupMapInstance = () => {

  if (mapControloo.value) {
    mapControloo.value.remove();
  }


  mapControloo.value = L.map('methanogen-map', {
    center: [30, 120],
    zoom: 3,
    minZoom: 3,
    maxZoom: 18,
    worldCopyJump: true,
    zoomAnimation: false,
    crs: L.CRS.EPSG3857
  });
};

const addBaseLayer = () => {
  L.tileLayer(
    'https://webrd0{s}.is.autonavi.com/appmaptile?lang=en&size=1&scale=1&style=8&x={x}&y={y}&z={z}',
    {
      subdomains: ['1', '2', '3', '4'],
      maxZoom: 18,
      minZoom: 2
    }
  ).addTo(mapControloo.value);
};

const addControls = () => {

  L.control.scale({
    imperial: false,
    position: 'bottomright'
  }).addTo(mapControloo.value);
};

const setupZoomHandlers = () => {

  mapControloo.value.on('zoomstart', () => {
    isZooming.value = true;
    Object.values(markers.value).forEach(marker => {
      if (marker && marker._icon) {
        marker._icon.style.display = 'none';
        if (marker._shadow) marker._shadow.style.display = 'none';
      }
    });
  });
  

  mapControloo.value.on('zoomend', () => {
    if (zoomTimer) clearTimeout(zoomTimer);
    zoomTimer = setTimeout(() => {
      isZooming.value = false;
      
      if (needsMarkerRefresh.value) {
        needsMarkerRefresh.value = false;
        safeAddMarkers();
      } else {
        Object.values(markers.value).forEach(marker => {
          if (marker && marker._icon) {
            marker._icon.style.display = '';
            if (marker._shadow) marker._shadow.style.display = '';
          }
        });
      }
    }, 300);
  });
};

const setupMoveHandlers = () => {

  mapControloo.value.on('moveend', () => {
    if (!isZooming.value) {
      Object.values(markers.value).forEach(marker => {
        if (marker && marker._icon) {
          marker._icon.style.display = '';
          if (marker._shadow) marker._shadow.style.display = '';
        }
      });
    }
  });
};

const setupErrorHandlers = () => {

  mapControloo.value.on('error', (e) => {
    needsMarkerRefresh.value = true;
    console.log(e)
    if (!isZooming.value) {
      safeAddMarkers();
    }
  });
};

const init_map = () => {
  setupMapInstance();
  addBaseLayer();
  addControls();
  setupZoomHandlers();
  setupMoveHandlers();
  setupErrorHandlers();
};

    // Helper function to safely clear all markers
    const clearAllMarkers = () => {
      try {
        if (!mapControloo.value) return;
        
        Object.values(markers.value).forEach(marker => {
          try {
            if (marker) {
              mapControloo.value.removeLayer(marker);
            }
          } catch (err) {
            console.warn('Error removing marker:', err);
          }
        });
        markers.value = {};
      } catch (error) {
        console.error('Error clearing markers:', error);
      }
    };

    // 在 script 部分添加环境图标生成函数
    const getEnvironmentIcon = (environmental_media, count) => {
      let color, symbol;
      
      // 根据环境类型决定颜色和符号
      if (!environmental_media) {
        color = '#666666';  // 灰色
        symbol = '●';
      } else {
        const env = environmental_media.toLowerCase();
        if (env.includes('water') || env.includes('marine') || env.includes('swamp')) {
          color = '#3b82f6';  // 蓝色 - 水环境
          symbol = '◆';
        } else if (env.includes('soil') || env.includes('sediment')) {
          color = '#92400e';  // 褐色 - 土壤/沉积物
          symbol = '▲';
        } else if (env.includes('sludge') || env.includes('digester') || env.includes('reactor')) {
          color = '#059669';  // 绿色 - 人工环境
          symbol = '■';
        } else {
          color = '#666666';  // 灰色 - 其他环境
          symbol = '●';
        }
      }

      return L.divIcon({
        className: 'custom-div-icon',
        html: `
          <div class="marker-pin" style="background-color: ${color}">
            ${count > 1 ? `<span class="marker-count">${count}</span>` : symbol}
          </div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12]
      });
    };

    // Safely add markers - with error handling
    const safeAddMarkers = debounce(() => {
      console.log('Safely adding markers');
      try {
        // First clear all existing markers
        clearAllMarkers();
        
        // Ensure map instance exists
        if (!mapControloo.value) {
          console.warn('Map instance does not exist, cannot add markers');
          return;
        }
        
        // Ensure we have data
        if (!mapData.value || mapData.value.length === 0) {
          console.warn('No map data available');
          return;
        }
        
        // Check again that we're not in the middle of zooming
        if (isZooming.value) {
          console.log('Map is zooming, delaying marker addition');
          needsMarkerRefresh.value = true;
          return;
        }

        // Group markers by coordinates
        const markerGroups = new Map();
        
        mapData.value.forEach(item => {
          try {
            // Validate coordinate data validity
            if (item.latitude === null || item.longitude === null || 
                isNaN(parseFloat(item.latitude)) || isNaN(parseFloat(item.longitude))) {
              console.warn(`Skipping invalid coordinate data: ${item.organism_name}`, item);
              return;
            }
            
            const latitude = Number(item.latitude);
            const longitude = Number(item.longitude);
            
            // Avoid invalid positions
            if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
              console.warn(`Coordinates out of range, skipping: ${item.organism_name}`);
              return;
            }
            
            // Create coordinate key for grouping
            const coordKey = `${latitude},${longitude}`;
            
            // Add item to the coordinate group
            if (!markerGroups.has(coordKey)) {
              markerGroups.set(coordKey, []);
            }
            markerGroups.get(coordKey).push(item);
            
          } catch (itemError) {
            console.error(`Error processing data item: ${item.organism_name || 'Unknown'}`, itemError);
          }
        });
        
        // Create markers for each coordinate group
        markerGroups.forEach((items, coordKey) => {
          try {
            const [latitude, longitude] = coordKey.split(',').map(Number);
            console.log(`Creating marker group at [${latitude}, ${longitude}] with ${items.length} items`);
            
            const marker = L.marker([latitude, longitude], {
              icon: getEnvironmentIcon(items[0].environmental_media, items.length),
              title: items.length > 1 ? `${items.length} organisms at this location` : items[0].organism_name
            });
            
            // Add to map
            try {
              marker.addTo(mapControloo.value);
            } catch (addErr) {
              console.error(`Error adding marker to map at [${latitude}, ${longitude}]`, addErr);
              return;
            }
            
            // Create popup content with all items at this location
            const popupContent = `
              <div class="map-popup">
                <h3>${items.length > 1 ? `${items.length} Organisms at This Location` : items[0].organism_name}</h3>
                ${items.map(item => `
                  <div class="popup-item">
                    <h4>${item.organism_name}</h4>
                    <p><strong>Location:</strong> ${item.location}</p>
                    <p><strong>Environment:</strong> ${item.environmental_media || 'Unknown'}</p>
                    <button class="popup-details-btn" data-id="${item.id}">View Details</button>
                  </div>
                `).join('<hr>')}
              </div>
            `;
            
            // Add popup
            try {
              marker.bindPopup(popupContent);
              
              // Show details dialog when clicking buttons in popup
              marker.getPopup().on('add', () => {
                setTimeout(() => {
                  const detailsBtns = document.querySelectorAll('.popup-details-btn');
                  detailsBtns.forEach(btn => {
                    btn.addEventListener('click', () => {
                      const itemId = btn.getAttribute('data-id');
                      const selectedItem = items.find(item => item.id === Number(itemId));
                      if (selectedItem) {
                        dialogVisible.value = true;
                        activeMarker.value = selectedItem;
                      }
                    });
                  });
                }, 100);
              });
            } catch (popupErr) {
              console.warn(`Error adding popup to marker at [${latitude}, ${longitude}]`, popupErr);
            }
            
            // Save marker references
            items.forEach(item => {
              if (item.id) {
                markers.value[item.id] = marker;
              }
            });
            
          } catch (groupError) {
            console.error(`Error creating marker group at [${coordKey}]`, groupError);
          }
        });
        
      } catch (outerError) {
        console.error('Serious error during marker addition:', outerError);
      }
    }, 200); // 200ms debounce delay

    // Get data from API
    const getgeographicdata = async () => {
      try {
        loading.value = true;
        const response = await axios.get('/api/geographic-data/');
        
        if (response.data && response.data.results) {
          mapData.value = response.data.results;
          // 统计环境类型数量
          getenvCounts();
          // Add markers after data is fetched
          safeAddMarkers();
          
          ElNotification({
            title: 'Success',
            message: `Loaded ${mapData.value.length} distribution points`,
            type: 'success',
            duration: 3000
          });
        }
      } catch (error) {
        console.error('Failed to fetch geographic data:', error);
        ElNotification({
          title: 'Error',
          message: 'Unable to load geographic data, please try again later',
          type: 'error'
        });
      } finally {
        loading.value = false;
      }
    };

    // 添加环境类型统计函数
    const getenvCounts = async () => {
      try {
        // 获取环境类型统计数据
        const response = await axios.get('/api/environment-counts/');
        if (response.data) {
          envCounts.value = response.data;
        }
      } catch (error) {
        console.error('Failed to fetch environment counts:', error);
        // 如果API调用失败，使用mapData计算
        const counts = {
          aquatic: 0,
          soil: 0,
          artificial: 0,
          other: 0
        };
        
        // 计算环境类型数量
        mapData.value.forEach(item => {
          if (!item.environmental_media) {
            counts.other++;
          } else {
            const env = item.environmental_media.toLowerCase();
            if (env.includes('water') || env.includes('marine') || env.includes('swamp')) {
              counts.aquatic++;
            } else if (env.includes('soil') || env.includes('sediment')) {
              counts.soil++;
            } else if (env.includes('sludge') || env.includes('digester') || env.includes('reactor')) {
              counts.artificial++;
            } else {
              counts.other++;
            }
          }
        });
        
        envCounts.value = counts;
      }
    };

    // Manually refresh data
    const refreshData = async () => {
      try {
        loading.value = true;
        const response = await axios.get('/api/refresh-geographic-data/');
        
        if (response.data && response.data.status === 'success') {
          ElNotification({
            title: 'Success',
            message: response.data.message,
            type: 'success'
          });
          
          // Refetch data
          getgeographicdata();
        }
      } catch (error) {
        console.error('Failed to refresh geographic data:', error);
        ElNotification({
          title: 'Error',
          message: 'Failed to refresh geographic data, please try again later',
          type: 'error'
        });
      } finally {
        loading.value = false;
      }
    };

    // Handle window resize
    const handleResize = debounce(() => {
      if (mapControloo.value) {
        mapControloo.value.invalidateSize();
        // May need to reposition markers after map size changes
        needsMarkerRefresh.value = true;
        safeAddMarkers();
      }
    }, 250); // 250ms debounce delay

    // Emergency recovery function - try to reset map when errors occur
    const recoverMap = () => {
      try {
        // Clear all markers
        clearAllMarkers();
        
        // If map instance exists, reset it
        if (mapControloo.value) {
          mapControloo.value.setView([30, 0], 2);
        } else {
          // Reinitialize map
          init_map();
        }
        
        // Re-add markers
        setTimeout(() => {
          safeAddMarkers();
        }, 500);
      } catch (error) {
        console.error('Error recovering map:', error);
      }
    };

    // Toggle fullscreen display
    const toggleFullscreen = () => {
      try {
        const mapContainer = document.querySelector('.world-map-container');
        
        if (!isFullscreen.value) {
          // Enter fullscreen
          if (mapContainer.requestFullscreen) {
            mapContainer.requestFullscreen();
          } else if (mapContainer.mozRequestFullScreen) { // Firefox
            mapContainer.mozRequestFullScreen();
          } else if (mapContainer.webkitRequestFullscreen) { // Chrome, Safari
            mapContainer.webkitRequestFullscreen();
          } else if (mapContainer.msRequestFullscreen) { // IE/Edge
            mapContainer.msRequestFullscreen();
          }
        } else {
          // Exit fullscreen
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.mozCancelFullScreen) { // Firefox
            document.mozCancelFullScreen();
          } else if (document.webkitExitFullscreen) { // Chrome, Safari
            document.webkitExitFullscreen();
          } else if (document.msExitFullscreen) { // IE/Edge
            document.msExitFullscreen();
          }
        }
      } catch (error) {
        console.error('Error toggling fullscreen state:', error);
        ElNotification({
          title: 'Error',
          message: 'Failed to toggle fullscreen state',
          type: 'error'
        });
      }
    };

    // Handle fullscreen state changes
    const handleFullscreenChange = () => {
      isFullscreen.value = !!(
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      
      // Adjust map size after fullscreen state changes
      setTimeout(() => {
        if (mapControloo.value) {
          mapControloo.value.invalidateSize();
        }
      }, 100);
    };

    // 修改代谢途径处理函数
    const splitPathways = (pathways) => {
      if (!pathways) return [];
      // 使用正则表达式匹配完整的代谢途径名称
      const pathwayRegex = /Primitive Methanogenesis Pathway|Wood-Ljungdahl pathway for methanogenesis|Reverse Wood-Ljungdahl pathway for methanogenesis/g;
      const matches = pathways.match(pathwayRegex);
      return matches || [];
    };

    const getPathwayImageUrl = (pathway) => {
      const pathwayMap = {
        'Primitive Methanogenesis Pathway': '/methanogens_project/frontend/public/images/Primitive_Methanogenesis_Pathway.png',
        'Wood-Ljungdahl pathway for methanogenesis': '/methanogens_project/frontend/public/images/Wood-Ljungdahl_pathway_for_methanogenesis.png',
        'Reverse Wood-Ljungdahl pathway for methanogenesis': '/methanogens_project/frontend/public/images/Reverse_Wood_Ljungdahl_pathway_for_methanogenesis.png'
      };
      
      const url = pathwayMap[pathway];
      if (!url) {
        console.warn(`No matching image for pathway: ${pathway}`);
        return '#';
      }
      return url;
    };

    // 修改图片打开函数
    const openPathwayImage = (pathway) => {
      const url = getPathwayImageUrl(pathway);
      if (url !== '#') {
        // 使用相对路径
        const relativePath = url.replace('/methanogens_project/frontend/public', '');
        window.open(relativePath, '_blank');
      }
    };

    // 添加DOI URL格式化函数
    const formatDoiUrl = (doi) => {
      if (!doi) return '#';
      // 移除可能存在的前缀
      const cleanDoi = doi.replace(/^(doi:|https?:\/\/(dx\.)?doi\.org\/)/, '').trim();
      // 对DOI进行URL编码并返回完整URL
      return `https://doi.org/${encodeURIComponent(cleanDoi)}`;
    };

    onMounted(() => {
      try {
        // Initialize map
        init_map();
        // Fetch data
        getgeographicdata();

        // Add window resize listener
        window.addEventListener('resize', handleResize);
        
        // Add fullscreen change event listeners
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        
        // Add global error handler
        window.addEventListener('error', (event) => {
          // Check if it's Leaflet-related error
          if (event.message && event.message.includes('leaflet') || 
              (event.error && event.error.stack && event.error.stack.includes('leaflet'))) {
            console.warn('Captured Leaflet-related error:', event.message);
            recoverMap();
          }
        });
      } catch (error) {
        console.error('Error during component mount:', error);
      }
    });

    onUnmounted(() => {
      try {
        // Clear timers
        if (zoomTimer) clearTimeout(zoomTimer);
        
        // Clean up map instance
        clearAllMarkers();
        if (mapControloo.value) {
          mapControloo.value.remove();
        }
        
        // Remove event listeners
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      } catch (error) {
        console.error('Error during component unmount:', error);
      }
    });

    // Return component properties and methods
    return {
      loading,
      mapData,
      dialogVisible,
      activeMarker,
      refreshData,
      recoverMap,  // Emergency recovery function
      isFullscreen,  // Fullscreen state
      toggleFullscreen,  // Toggle fullscreen function
      splitPathways,
      getPathwayImageUrl,
      openPathwayImage,
      formatDoiUrl,  // 添加到返回对象中
      envCounts, // 环境类型计数
    };
  }
};
</script>

<style>
/* 导入字体 */
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700&family=Inter:wght@400;500&family=Open+Sans:wght@400;600&family=Roboto:wght@400;500&display=swap');

.world-map-container {
  width: 100%;
  margin: 20px 0;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.section-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1a1a1a;
  text-align: center;
  letter-spacing: -0.5px;
}

.map-wrapper {
  position: relative;
  width: 100%;
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.map-container {
  width: 100%;
  height: 100%;
  z-index: 1;
  will-change: transform; /* 提高渲染性能 */
  transform: translateZ(0); /* 启用GPU加速 */
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

.loading-icon {
  font-size: 36px;
  color: #2563eb;
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Popup styles */
.map-popup {
  padding: 12px;
  max-width: 300px;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Open Sans', sans-serif;
}

.map-popup h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #1a1a1a;
  text-align: center;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
}

.map-popup h4 {
  margin: 8px 0;
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
}

.popup-item {
  margin: 8px 0;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.popup-item p {
  margin: 5px 0;
  font-size: 13px;
  color: #4a5568;
  line-height: 1.5;
}

.popup-details-btn {
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;
  font-size: 13px;
  transition: background-color 0.3s;
  width: 100%;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.popup-details-btn:hover {
  background-color: #1d4ed8;
}

hr {
  margin: 10px 0;
  border: none;
  border-top: 1px solid #eee;
}

/* Details dialog styles */
.marker-details {
  padding: 15px;
  font-family: 'Open Sans', sans-serif;
}

.detail-row {
  margin-bottom: 15px;
  display: flex;
}

.detail-label {
  font-weight: 600;
  color: #2d3748;
  width: 120px;
  flex-shrink: 0;
}

.detail-value {
  color: #4a5568;
  flex: 1;
  line-height: 1.5;
}

.references {
  white-space: pre-line;
  line-height: 1.6;
  font-size: 13px;
}

.doi-link {
  color: #2563eb;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

.doi-link:hover {
  text-decoration: underline;
}

.map-reset-btn {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 999;
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.map-reset-btn:hover {
  background-color: #f0f0f0;
}

.map-reset-btn:active {
  background-color: #e8e8e8;
}

.map-fullscreen-btn {
  position: absolute;
  bottom: 10px;
  left: 150px;
  z-index: 999;
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.map-fullscreen-btn:hover {
  background-color: #f0f0f0;
}

.map-fullscreen-btn:active {
  background-color: #e8e8e8;
}

/* Fullscreen mode styles */
.fullscreen-mode {
  padding: 0;
  margin: 0;
  height: 100vh;
  width: 100vw;
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
}

.fullscreen-mode .map-wrapper {
  height: 100vh;
  border-radius: 0;
  box-shadow: none;
}

/* Adapt to different browser fullscreen styles */
:fullscreen .world-map-container,
:-webkit-full-screen .world-map-container,
:-moz-full-screen .world-map-container,
:-ms-fullscreen .world-map-container {
  padding: 0;
  margin: 0;
  height: 100%;
  width: 100%;
}

:fullscreen .map-wrapper,
:-webkit-full-screen .map-wrapper,
:-moz-full-screen .map-wrapper,
:-ms-fullscreen .map-wrapper {
  height: 100%;
  border-radius: 0;
  box-shadow: none;
}

/* 添加新的标记样式 */
.custom-div-icon {
  background: none;
  border: none;
}

.marker-pin {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 0 4px rgba(0,0,0,0.3);
  font-size: 12px;
  color: white;
  transition: all 0.3s ease;
}

.marker-pin:hover {
  transform: scale(1.1);
  box-shadow: 0 0 6px rgba(0,0,0,0.4);
}

.marker-count {
  font-size: 10px;
  font-weight: bold;
  color: white;
}

/* 地图图例样式 */
.map-legend {
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  z-index: 1000;
  font-family: 'Inter', sans-serif;
}

.map-legend h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin: 6px 0;
  font-size: 13px;
  color: #4b5563;
  font-weight: 500;
}

.legend-marker {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-size: 14px;
}

.legend-marker.water {
  color: #3b82f6;
}

.legend-marker.soil {
  color: #92400e;
}

.legend-marker.artificial {
  color: #059669;
}

.legend-marker.other {
  color: #666666;
}

/* 全屏模式下的图例位置调整 */
.fullscreen-mode .map-legend {
  top: 20px;
  right: 20px;
}

/* 更新代谢途径链接样式 */
.metabolic-pathways {
  display: block;
  line-height: 1.6;
}

.pathway-container {
  display: inline;
}

.pathway-link {
  color: #2563eb;
  text-decoration: none;
  transition: all 0.2s ease;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
}

.pathway-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
  background-color: rgba(37, 99, 235, 0.1);
}

.pathway-link:active {
  color: #1e40af;
}

.pathway-separator {
  margin: 0 4px;
  color: #6b7280;
}
</style> 